/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Loader2, Copy } from 'lucide-react';
import { generateSoapSummary } from '@/lib/ai_clinical';

/**
 * SoapSummaryPanel — integrated in VideoRoom notes panel.
 * Given raw notes/transcription, generates SOAP structured summary.
 */
export const SoapSummaryPanel = ({ apptId, sourceText, patientContext, onSummaryReady }) => {
  const [loading, setLoading] = useState(false);
  const [soap, setSoap] = useState(null);

  const run = async () => {
    if (!sourceText || sourceText.trim().length < 10) {
      toast.error('Aggiungi almeno una nota clinica prima di generare il SOAP');
      return;
    }
    setLoading(true);
    try {
      const res = await generateSoapSummary({
        transcript_or_notes: sourceText,
        appointment_id: apptId,
        patient_context: patientContext,
      });
      setSoap(res.result);
      if (onSummaryReady) onSummaryReady(res.result);
      toast.success('SOAP generato');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore generazione SOAP');
    } finally {
      setLoading(false);
    }
  };

  const copyAll = async () => {
    if (!soap) return;
    const text = [
      `S — ${soap.subjective || ''}`,
      `O — ${soap.objective || ''}`,
      `A — ${soap.assessment || ''}`,
      `P — ${soap.plan || ''}`,
    ].join('\n\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copiato negli appunti');
    } catch {
      toast.error('Copia non disponibile');
    }
  };

  return (
    <div className="space-y-3" data-testid="soap-panel">
      <div className="flex items-center gap-2">
        <Button size="sm" variant="secondary" onClick={run} disabled={loading} data-testid="soap-generate-btn">
          {loading ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generazione…</> : <><Sparkles className="h-3.5 w-3.5" /> Genera SOAP</>}
        </Button>
        {soap && (
          <Button size="sm" variant="ghost" onClick={copyAll} data-testid="soap-copy-btn"><Copy className="h-3.5 w-3.5" /> Copia</Button>
        )}
      </div>

      <AnimatePresence>
        {soap && (
          <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="space-y-2">
            {['subjective','objective','assessment','plan'].map((k) => (
              soap[k] ? (
                <div key={k} className="rounded-lg border border-border/60 p-2.5 text-xs" data-testid={`soap-${k}`}>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-primary mb-0.5">{k === 'subjective' ? 'S — Soggettivo' : k === 'objective' ? 'O — Oggettivo' : k === 'assessment' ? 'A — Assessment' : 'P — Piano'}</div>
                  <div className="whitespace-pre-wrap leading-relaxed">{soap[k]}</div>
                </div>
              ) : null
            ))}
            {soap.tasks?.length > 0 && (
              <div className="rounded-lg border border-border/60 p-2.5">
                <div className="text-[10px] uppercase tracking-wider font-bold text-primary mb-1">Task</div>
                <ul className="text-xs space-y-1">
                  {soap.tasks.map((t) => (
                    <li key={t.title} className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">{t.priority}</Badge>
                      <span>{t.title}</span>
                      {t.due_days != null && <span className="text-muted-foreground text-[10px]">+{t.due_days}gg</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {soap.red_flags_detected?.length > 0 && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs">
                <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700 dark:text-amber-300 mb-1">Segnali da monitorare</div>
                <ul className="list-disc pl-4">
                  {soap.red_flags_detected.map((r) => <li key={r}>{r}</li>)}
                </ul>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
